import { cn } from '@/lib/utils';

interface VoicePartBadge {
  id?: string;
  name: string;
  sortOrder: number;
}

interface VoicePartBadgesProps {
  voiceParts: VoicePartBadge[];
  maxVisible?: number;
  compact?: boolean;
  className?: string;
}

// Kleuren per sortOrder range (uit design reference)
const VOICE_PART_COLORS: Record<string, string> = {
  sopraan: '#FF6B6B',    // 1-9
  alt: '#4ECDC4',        // 10-19
  tenor: '#45B7D1',      // 20-29
  bas: '#96CEB4',        // 30-39
  alternative: '#DDA0DD', // 40-49 (High/Mid/Low)
  all: '#1DB954',        // 50-59 (All Voices)
  backtrack: '#888888',  // 60+
};

// Afkortingen voor compacte weergave
const ABBREVIATIONS: Record<string, string> = {
  'Sopraan': 'S',
  'Sopraan 1': 'S1',
  'Sopraan 2': 'S2',
  'Alt': 'A',
  'Alt 1': 'A1',
  'Alt 2': 'A2',
  'Tenor': 'T',
  'Tenor 1': 'T1',
  'Tenor 2': 'T2',
  'Bas': 'B',
  'Bas 1': 'B1',
  'Bas 2': 'B2',
  'High': 'Hi',
  'Mid': 'Mid',
  'Low': 'Lo',
  'All Voices': 'All',
  'All voices': 'All',
  'Backtrack': 'BT',
};

/**
 * Get color for a voice part based on sortOrder
 */
export function getVoicePartColor(sortOrder: number): string {
  if (sortOrder < 10) return VOICE_PART_COLORS.sopraan;
  if (sortOrder < 20) return VOICE_PART_COLORS.alt;
  if (sortOrder < 30) return VOICE_PART_COLORS.tenor;
  if (sortOrder < 40) return VOICE_PART_COLORS.bas;
  if (sortOrder < 50) return VOICE_PART_COLORS.alternative;
  if (sortOrder < 60) return VOICE_PART_COLORS.all;
  return VOICE_PART_COLORS.backtrack;
}

/**
 * Get abbreviation for a voice part name
 */
export function getVoicePartAbbreviation(name: string): string {
  return ABBREVIATIONS[name] || name.substring(0, 2);
}

export function VoicePartBadges({
  voiceParts,
  maxVisible = 5,
  compact = true,
  className,
}: VoicePartBadgesProps) {
  if (!voiceParts || voiceParts.length === 0) {
    return null;
  }

  // Sort by sortOrder
  const sorted = [...voiceParts].sort((a, b) => a.sortOrder - b.sortOrder);
  const visible = sorted.slice(0, maxVisible);
  const remaining = sorted.length - maxVisible;

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {visible.map((vp, index) => {
        const color = getVoicePartColor(vp.sortOrder);
        const label = compact ? getVoicePartAbbreviation(vp.name) : vp.name;

        return (
          <span
            key={vp.id || index}
            className={cn(
              'inline-flex items-center justify-center',
              'text-xs font-medium rounded-full',
              'transition-transform hover:scale-105',
              compact ? 'px-1.5 py-0.5 min-w-[24px]' : 'px-2 py-0.5'
            )}
            style={{
              backgroundColor: `${color}25`,
              color: color,
              border: `1px solid ${color}40`,
            }}
            title={vp.name}
          >
            {label}
          </span>
        );
      })}
      {remaining > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center',
            'text-xs font-medium rounded-full',
            'px-1.5 py-0.5 min-w-[24px]',
            'bg-bg-elevated text-text-muted'
          )}
          title={sorted.slice(maxVisible).map(vp => vp.name).join(', ')}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}

/**
 * Single voice part badge (for use in player, etc.)
 */
export function VoicePartBadge({
  name,
  sortOrder,
  compact = false,
  className,
}: {
  name: string;
  sortOrder: number;
  compact?: boolean;
  className?: string;
}) {
  const color = getVoicePartColor(sortOrder);
  const label = compact ? getVoicePartAbbreviation(name) : name;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'text-xs font-medium rounded-full',
        compact ? 'px-1.5 py-0.5 min-w-[24px]' : 'px-2 py-1',
        className
      )}
      style={{
        backgroundColor: `${color}25`,
        color: color,
        border: `1px solid ${color}40`,
      }}
      title={name}
    >
      {label}
    </span>
  );
}
